/* eslint-disable max-lines */
import { memo, useMemo, useState } from "react"

import { useTranslation } from "react-i18next"

import type { TrainingStatus, TrainingUserProgress } from "../types"

import { GraduationCap, RefreshCw, Users } from "@/components/icons"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { EmptyState } from "@/components/ui/empty-state"
import { Progress } from "@/components/ui/progress"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { SortableTableHeader } from "@/components/ui/sortable-table-header"
import { Table, TableBody, TableCell, TableHeader, TableRow } from "@/components/ui/table"
import { useRoutes } from "@/contexts/use-routes"
import { LoadingCard } from "@/features/admin-dashboard/components/admin-dashboard-loading"

import { MobileSortControls } from "./mobile-sort-controls"
import { TrainingCompletionRadar } from "./training-completion-radar"

type TrainingPeriodId =
  | "last7Days"
  | "last30Days"
  | "last90Days"
  | "lastMonth"
  | "thisMonth"
  | "allTime"

type TrainingPeriodPreset = {
  id: TrainingPeriodId
  labelKey: string
  days: number | null // null means all time
}

const TRAINING_PERIOD_PRESETS: TrainingPeriodPreset[] = [
  { id: "last7Days", labelKey: "adminDashboard.training.period.last7Days", days: 7 },
  { id: "last30Days", labelKey: "adminDashboard.training.period.last30Days", days: 30 },
  { id: "last90Days", labelKey: "adminDashboard.training.period.last90Days", days: 90 },
  { id: "lastMonth", labelKey: "adminDashboard.training.period.lastMonth", days: 30 },
  { id: "thisMonth", labelKey: "adminDashboard.training.period.thisMonth", days: 30 },
  { id: "allTime", labelKey: "adminDashboard.training.period.allTime", days: null },
]

const INITIAL_VISIBLE_COUNT = 10

const statusLabelMap: Record<string, string> = {
  completed: "adminDashboard.training.status.completed",
  inProgress: "adminDashboard.training.status.inProgress",
  notStarted: "adminDashboard.training.status.notStarted",
}

type SortState = {
  key: "user" | "progress" | "status" | "modules" | "activity" | null
  direction: "asc" | "desc" | null
}

function getDisplayName(user: TrainingUserProgress): string {
  return user.name || user.email || user.userId || user.alias || ""
}

function formatActivityDate(value?: string | null) {
  if (!value) return "—"
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return "—"
  return date.toLocaleDateString("sv-SE", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  })
}

function filterUsersByPeriod(
  users: TrainingUserProgress[],
  periodId: TrainingPeriodId
): TrainingUserProgress[] {
  const preset = TRAINING_PERIOD_PRESETS.find((p) => p.id === periodId)
  if (!preset || preset.days === null) return users

  // For lastMonth/thisMonth, calculate actual date ranges
  const now = new Date()
  let cutoffDate: Date

  if (periodId === "lastMonth") {
    // First day of last month
    cutoffDate = new Date(now.getFullYear(), now.getMonth() - 1, 1)
  } else if (periodId === "thisMonth") {
    // First day of this month
    cutoffDate = new Date(now.getFullYear(), now.getMonth(), 1)
  } else {
    // Standard days-based filter
    cutoffDate = new Date()
    cutoffDate.setDate(cutoffDate.getDate() - preset.days)
  }

  return users.filter((user) => {
    // Include users with activity after cutoff, or users who haven't started yet
    if (!user.lastActivity) return true
    const activityDate = new Date(user.lastActivity)
    return activityDate >= cutoffDate
  })
}

export const TrainingStatusCard = memo(function TrainingStatusCard({
  data,
  isLoading,
  isFetching,
  onRefresh,
}: {
  data: TrainingStatus | null
  isLoading: boolean
  isFetching?: boolean
  onRefresh?: () => Promise<unknown>
}) {
  const { t } = useTranslation()
  const { trainingModules } = useRoutes()
  const [sort, setSort] = useState<SortState>({ key: "progress", direction: "desc" })
  const [visibleCount, setVisibleCount] = useState(INITIAL_VISIBLE_COUNT)
  const [periodId, setPeriodId] = useState<TrainingPeriodId>("allTime")

  const moduleMetaLookup = useMemo(() => {
    const entries = new Map<
      string,
      {
        moduleId: string
        moduleName: string
        sectionName: string
      }
    >()
    trainingModules.forEach((module) => {
      module.sections.forEach((section) => {
        entries.set(section.id, {
          moduleId: module.id,
          moduleName: module.display_name,
          sectionName: section.display_name,
        })
      })
    })
    return entries
  }, [trainingModules])

  const moduleOrderLookup = useMemo(() => {
    return new Map(trainingModules.map((module, index) => [module.id, index]))
  }, [trainingModules])

  const points = data?.points ?? []
  const allUsers = data?.users ?? []
  const originalSummary = data?.summary

  // Filter users based on selected period
  const users = useMemo(() => filterUsersByPeriod(allUsers, periodId), [allUsers, periodId])

  // Recalculate summary based on filtered users
  const summary = useMemo(() => {
    if (!originalSummary || periodId === "allTime") return originalSummary
    if (users.length === 0) return null

    const totalUsers = users.length
    const completedUsers = users.filter((u) => u.status === "completed").length
    const inProgressUsers = users.filter((u) => u.status === "inProgress").length
    const notStartedUsers = users.filter((u) => u.status === "notStarted").length
    const progressValues = users.map((u) => u.progressPct)
    const avgCompletionPct = progressValues.reduce((a, b) => a + b, 0) / totalUsers
    const sortedProgress = [...progressValues].sort((a, b) => a - b)
    const medianCompletionPct =
      sortedProgress.length % 2 === 0
        ? (sortedProgress[sortedProgress.length / 2 - 1] +
            sortedProgress[sortedProgress.length / 2]) /
          2
        : sortedProgress[Math.floor(sortedProgress.length / 2)]

    return {
      ...originalSummary,
      totalUsers,
      completedUsers,
      inProgressUsers,
      notStartedUsers,
      avgCompletionPct,
      medianCompletionPct,
    }
  }, [originalSummary, users, periodId])

  const moduleChartData = useMemo(() => {
    const grouped = new Map<
      string,
      {
        moduleId: string
        label: string
        completedUsers: number
        inProgressUsers: number
        notStartedUsers: number
      }
    >()

    points.forEach((point) => {
      const meta = moduleMetaLookup.get(point.moduleId)
      const categoryId = meta?.moduleId ?? point.moduleId
      const label = meta?.moduleName || point.moduleName || meta?.sectionName || point.moduleId

      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      const completedUsers = Number(point.completedUsers ?? 0)
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      const inProgressUsers = Number(point.inProgressUsers ?? 0)
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      const notStartedUsers = Number(point.notStartedUsers ?? 0)

      const entry = grouped.get(categoryId) ?? {
        moduleId: categoryId,
        label,
        completedUsers: 0,
        inProgressUsers: 0,
        notStartedUsers: 0,
      }

      entry.completedUsers += completedUsers
      entry.inProgressUsers += inProgressUsers
      entry.notStartedUsers += notStartedUsers
      entry.label = entry.label || label

      grouped.set(categoryId, entry)
    })

    const ordered = Array.from(grouped.values()).sort((a, b) => {
      const orderA = moduleOrderLookup.get(a.moduleId)
      const orderB = moduleOrderLookup.get(b.moduleId)
      if (orderA !== undefined && orderB !== undefined) return orderA - orderB
      if (orderA !== undefined) return -1
      if (orderB !== undefined) return 1
      return a.label.localeCompare(b.label)
    })

    return ordered.map((entry) => {
      const totalUsers = entry.completedUsers + entry.inProgressUsers + entry.notStartedUsers
      return {
        moduleId: entry.moduleId,
        label: entry.label,
        completedPct: totalUsers > 0 ? (entry.completedUsers / totalUsers) * 100 : 0,
        inProgressPct: totalUsers > 0 ? (entry.inProgressUsers / totalUsers) * 100 : 0,
        notStartedPct: totalUsers > 0 ? (entry.notStartedUsers / totalUsers) * 100 : 0,
        completedUsers: entry.completedUsers,
        inProgressUsers: entry.inProgressUsers,
        notStartedUsers: entry.notStartedUsers,
      }
    })
  }, [points, moduleMetaLookup, moduleOrderLookup])

  const sortedUsers = useMemo(() => {
    if (!sort.key || !sort.direction) return users
    return [...users].sort((a, b) => {
      let aValue: string | number
      let bValue: string | number

      switch (sort.key) {
        case "user":
          aValue = getDisplayName(a).toLowerCase()
          bValue = getDisplayName(b).toLowerCase()
          break
        case "progress":
          aValue = a.progressPct
          bValue = b.progressPct
          break
        case "modules":
          aValue = a.modulesCompleted ?? 0
          bValue = b.modulesCompleted ?? 0
          break
        case "status":
          aValue = a.status ?? ""
          bValue = b.status ?? ""
          break
        case "activity":
          aValue = a.lastActivity ? new Date(a.lastActivity).getTime() : 0
          bValue = b.lastActivity ? new Date(b.lastActivity).getTime() : 0
          break
        default:
          return 0
      }

      if (typeof aValue === "string" && typeof bValue === "string") {
        return sort.direction === "asc"
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue)
      }

      return sort.direction === "asc"
        ? (aValue as number) - (bValue as number)
        : (bValue as number) - (aValue as number)
    })
  }, [users, sort])

  const visibleUsers = useMemo(
    () => sortedUsers.slice(0, visibleCount),
    [sortedUsers, visibleCount]
  )

  const hasMore = sortedUsers.length > visibleCount
  const isExpanded = visibleCount > INITIAL_VISIBLE_COUNT && visibleCount >= sortedUsers.length

  const handleSort = (key: SortState["key"]) => {
    if (!key) return
    setSort((prev) => {
      if (prev.key === key) {
        if (prev.direction === "asc") return { key, direction: "desc" }
        if (prev.direction === "desc") return { key: null, direction: null }
        return { key, direction: "asc" }
      }
      return { key, direction: "asc" }
    })
  }

  if (isLoading && users.length === 0) {
    return (
      <LoadingCard
        className="rounded-lg shadow-sm"
        icon={GraduationCap}
        rows={6}
        title={t("adminDashboard.training.title")}
        variant="table-row"
      />
    )
  }

  if (!isLoading && (!data || users.length === 0)) {
    return (
      <Card className="rounded-lg shadow-sm">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <GraduationCap className="size-5" />
              {t("adminDashboard.training.title")}
            </CardTitle>
            <div className="flex items-center gap-2">
              <Select value={periodId} onValueChange={(v) => setPeriodId(v as TrainingPeriodId)}>
                <SelectTrigger className="h-8 w-36 rounded-lg text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TRAINING_PERIOD_PRESETS.map((preset) => (
                    <SelectItem key={preset.id} value={preset.id}>
                      {t(preset.labelKey)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {onRefresh && (
                <Button size="icon" variant="ghost" onClick={() => onRefresh()}>
                  <RefreshCw className={`size-4 ${isFetching ? "animate-spin" : ""}`} />
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <EmptyState
            description={t("adminDashboard.training.emptyDescription")}
            icon={GraduationCap}
            title={t("adminDashboard.training.emptyTitle")}
          />
        </CardContent>
      </Card>
    )
  }

  return (
    <Card className="rounded-lg shadow-sm">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <GraduationCap className="size-5" />
            {t("adminDashboard.training.title")}
          </CardTitle>
          <div className="flex items-center gap-2">
            <Select value={periodId} onValueChange={(v) => setPeriodId(v as TrainingPeriodId)}>
              <SelectTrigger className="h-8 w-36 rounded-lg text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {TRAINING_PERIOD_PRESETS.map((preset) => (
                  <SelectItem key={preset.id} value={preset.id}>
                    {t(preset.labelKey)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {onRefresh && (
              <Button size="icon" variant="ghost" onClick={() => onRefresh()}>
                <RefreshCw className={`size-4 ${isFetching ? "animate-spin" : ""}`} />
              </Button>
            )}
          </div>
        </div>
        {summary && (
          <div className="text-muted-foreground text-sm">
            {t("adminDashboard.training.subtitle", {
              totalUsers: summary.totalUsers,
              modulesTotal: summary.modulesTotal,
            })}
          </div>
        )}
      </CardHeader>
      <CardContent className="space-y-6">
        {summary && (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <div className="rounded-lg border p-3">
              <div className="text-muted-foreground text-xs">
                {t("adminDashboard.training.kpis.overall")}
              </div>
              <div className="text-lg font-semibold">{summary.avgCompletionPct.toFixed(1)}%</div>
              <div className="text-muted-foreground text-xs">
                {t("adminDashboard.training.kpis.median", {
                  value: summary.medianCompletionPct.toFixed(1),
                })}
              </div>
            </div>
            <div className="rounded-lg border p-3">
              <div className="text-muted-foreground text-xs">
                {t("adminDashboard.training.kpis.completed")}
              </div>
              <div className="text-lg font-semibold">{summary.completedUsers}</div>
              <div className="text-muted-foreground text-xs">
                {t("adminDashboard.training.kpis.completedModules", {
                  value: summary.modulesCompleted,
                })}
              </div>
            </div>
            <div className="rounded-lg border p-3">
              <div className="text-muted-foreground text-xs">
                {t("adminDashboard.training.kpis.inProgress")}
              </div>
              <div className="text-lg font-semibold">{summary.inProgressUsers}</div>
              <div className="text-muted-foreground text-xs">
                {t("adminDashboard.training.kpis.inProgressModules", {
                  value: summary.modulesInProgress,
                })}
              </div>
            </div>
            <div className="rounded-lg border p-3">
              <div className="text-muted-foreground text-xs">
                {t("adminDashboard.training.kpis.notStarted")}
              </div>
              <div className="text-lg font-semibold">{summary.notStartedUsers}</div>
              <div className="text-muted-foreground text-xs">
                {t("adminDashboard.training.kpis.notStartedModules", {
                  value: summary.modulesNotStarted,
                })}
              </div>
            </div>
          </div>
        )}

        {moduleChartData.length > 0 && (
          <section className="space-y-3">
            <div className="text-muted-foreground text-sm font-medium">
              {t("adminDashboard.training.pointsTitle")}
            </div>
            <TrainingCompletionRadar data={moduleChartData} />
          </section>
        )}

        <section className="space-y-3">
          <div className="text-muted-foreground flex items-center gap-2 text-sm font-medium">
            <Users className="size-4" />
            {t("adminDashboard.training.table.title")}
          </div>
          <div className="space-y-2 lg:hidden">
            <MobileSortControls
              ariaLabel="Sort training users"
              direction={sort.direction}
              sortKey={sort.key}
              options={[
                { id: "user", label: t("adminDashboard.training.table.headers.user") },
                { id: "progress", label: t("adminDashboard.training.table.headers.progress") },
                { id: "modules", label: t("adminDashboard.training.table.headers.modules") },
                { id: "status", label: t("adminDashboard.training.table.headers.status") },
                {
                  id: "activity",
                  label: t("adminDashboard.training.table.headers.lastActivity"),
                },
              ]}
              onSortChange={(key, direction) =>
                setSort({ key: key as Exclude<SortState["key"], null>, direction })
              }
            />
            {visibleUsers.map((user) => {
              const completed = user.modulesCompleted ?? 0
              const total = user.modulesTotal ?? 0
              return (
                <div
                  key={`${user.userId}-mobile`}
                  className="bg-card space-y-3 rounded-lg border p-3 text-sm"
                  data-testid="training-user-mobile-row"
                >
                  <div className="truncate font-medium" title={getDisplayName(user)}>
                    {getDisplayName(user)}
                  </div>
                  <div>
                    <div className="mb-1 flex items-center justify-between gap-3 text-xs">
                      <span className="text-muted-foreground">Progress</span>
                      <span className="font-semibold tabular-nums">
                        {user.progressPct.toFixed(1)}%
                      </span>
                    </div>
                    <Progress className="h-1.5" value={user.progressPct} />
                  </div>
                  <div className="grid grid-cols-2 gap-3 text-xs">
                    <div>
                      <span className="text-muted-foreground block">Modules</span>
                      <span className="font-medium tabular-nums">
                        {total > 0 ? `${completed}/${total}` : "—"}
                      </span>
                    </div>
                    <div>
                      <span className="text-muted-foreground block">Status</span>
                      <span className="font-medium">
                        {t(statusLabelMap[user.status ?? "notStarted"])}
                      </span>
                    </div>
                  </div>
                  <p className="text-muted-foreground text-xs">
                    Last activity: {formatActivityDate(user.lastActivity)}
                  </p>
                </div>
              )
            })}
          </div>
          <div className="hidden lg:block">
            <Table>
              <TableHeader>
                <TableRow>
                  <SortableTableHeader
                    align="left"
                    className="min-w-[220px]"
                    currentSort={sort}
                    sortKey="user"
                    onSort={handleSort}
                  >
                    {t("adminDashboard.training.table.headers.user")}
                  </SortableTableHeader>
                  <SortableTableHeader
                    align="right"
                    className="min-w-[140px]"
                    currentSort={sort}
                    sortKey="progress"
                    onSort={handleSort}
                  >
                    {t("adminDashboard.training.table.headers.progress")}
                  </SortableTableHeader>
                  <SortableTableHeader
                    align="right"
                    className="min-w-[120px]"
                    currentSort={sort}
                    sortKey="modules"
                    onSort={handleSort}
                  >
                    {t("adminDashboard.training.table.headers.modules")}
                  </SortableTableHeader>
                  <SortableTableHeader
                    align="right"
                    className="min-w-[120px]"
                    currentSort={sort}
                    sortKey="status"
                    onSort={handleSort}
                  >
                    {t("adminDashboard.training.table.headers.status")}
                  </SortableTableHeader>
                  <SortableTableHeader
                    align="right"
                    className="min-w-[120px]"
                    currentSort={sort}
                    sortKey="activity"
                    onSort={handleSort}
                  >
                    {t("adminDashboard.training.table.headers.lastActivity")}
                  </SortableTableHeader>
                </TableRow>
              </TableHeader>
              <TableBody>
                {visibleUsers.map((user) => {
                  const completed = user.modulesCompleted ?? 0
                  const total = user.modulesTotal ?? 0
                  return (
                    <TableRow key={user.userId}>
                      <TableCell className="max-w-[260px] min-w-[220px]">
                        <div className="truncate font-medium" title={getDisplayName(user)}>
                          {getDisplayName(user)}
                        </div>
                      </TableCell>
                      <TableCell className="min-w-[140px]">
                        <div className="flex flex-col items-end gap-1">
                          <span className="text-right tabular-nums">
                            {user.progressPct.toFixed(1)}%
                          </span>
                          <Progress
                            className="h-1.5 w-full max-w-[140px]"
                            value={user.progressPct}
                          />
                        </div>
                      </TableCell>
                      <TableCell className="min-w-[120px] text-right tabular-nums">
                        {total > 0 ? `${completed}/${total}` : "—"}
                      </TableCell>
                      <TableCell className="text-muted-foreground min-w-[120px] text-right text-sm">
                        {t(statusLabelMap[user.status ?? "notStarted"])}
                      </TableCell>
                      <TableCell className="text-muted-foreground min-w-[120px] text-right text-sm tabular-nums">
                        {formatActivityDate(user.lastActivity)}
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          </div>
        </section>
        {hasMore && (
          <button
            className="hover:bg-accent w-full rounded-lg border px-3 py-2 text-sm font-medium"
            type="button"
            onClick={() => setVisibleCount(sortedUsers.length)}
          >
            {t("adminDashboard.training.table.showMore", {
              count: sortedUsers.length - visibleCount,
            })}
          </button>
        )}
        {isExpanded && (
          <button
            className="hover:bg-accent w-full rounded-lg border px-3 py-2 text-sm font-medium"
            type="button"
            onClick={() => setVisibleCount(INITIAL_VISIBLE_COUNT)}
          >
            {t("adminDashboard.training.table.showLess")}
          </button>
        )}
      </CardContent>
    </Card>
  )
})
